'use client';

import Image from 'next/image';
import { motion } from 'framer-motion';
import { stegaClean } from '@sanity/client/stega';

import { GroqImage } from '@/helpers/Image';
import { resolveI18nText } from '@/sanity/globals/I18n';
import { PageBlogPostSchema } from '@/sanity/pages/PageBlogPostTemplate';
import heroMobileBackground from '@/public/global/hero-mobile-background.png';
import heroDesktopBackground from '@/public/global/hero_desktop_background-lg-2.png';
import { formatBlogDate } from './formatBlogDate';
import { tidyTitle } from './tidyTitle';

const ease = [0.25, 0.1, 0.25, 1] as const;

export default function BlogPostHero({
  data,
  language,
}: {
  data: PageBlogPostSchema;
  language: string;
}) {
  const category = data.category
    ? resolveI18nText(data.category.name, data.category.nameI18n, language)
    : '';
  const date = data.publishAt ? formatBlogDate(data.publishAt, language) : '';
  const hotspot = data.mainImageHotspot;

  return (
    <>
      <section className="relative w-full overflow-x-clip max-lg:pb-[2.5rem] max-lg:pt-[9rem] lg:pb-[6.625rem] lg:pt-[14rem]">
        <motion.div
          initial={{ opacity: 0, filter: 'blur(8px)' }}
          animate={{ opacity: 1, filter: 'blur(0px)' }}
          transition={{ duration: 1.2, ease }}
          className="pointer-events-none absolute top-0 z-0 h-full w-full max-lg:block lg:hidden"
        >
          <Image
            src={heroMobileBackground}
            alt=""
            className="h-full w-full object-cover object-center"
          />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, filter: 'blur(8px)' }}
          animate={{ opacity: 1, filter: 'blur(0px)' }}
          transition={{ duration: 1.2, ease }}
          className="pointer-events-none absolute left-0 top-0 z-0 h-[130rem] w-full max-lg:hidden lg:block"
        >
          <Image
            src={heroDesktopBackground}
            alt=""
            className="h-full w-full object-cover object-center"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease }}
          className="relative z-10 mx-auto flex flex-col items-center max-lg:w-[min(170.5rem,calc(100%-4rem))] max-lg:gap-[3rem] lg:w-[min(170.5rem,calc(100%-8rem))] lg:gap-[1.75rem]"
        >
          {(category || date) && (
            <div className="flex items-center gap-[2.5rem]">
              {category && (
                <span className="rounded-[0.25rem] bg-[#0b6d51] px-[1.25rem] py-[0.625rem] font-geist text-[1.75rem] leading-[1.3] tracking-[-0.0525rem] text-white">
                  {category}
                </span>
              )}
              {date && (
                <time
                  dateTime={stegaClean(data.publishAt)}
                  className="font-geist text-[2.25rem] leading-[1.3] tracking-[-0.0675rem] text-[#103037] opacity-50"
                >
                  {date}
                </time>
              )}
            </div>
          )}

          {data.title && (
            <h1 className="w-[min(99.625rem,100%)] text-center font-geist font-medium leading-[1.2] text-black max-lg:text-[4.5rem] max-lg:tracking-[-0.135rem] lg:text-[6rem] lg:tracking-[-0.18rem]">
              {tidyTitle(data.title)}
            </h1>
          )}
        </motion.div>
      </section>

      {data.mainImage?.src && (
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease, delay: 0.2 }}
          className="relative z-10 mx-auto overflow-hidden rounded-[0.25rem] max-lg:aspect-[401/350] max-lg:w-[min(176.375rem,calc(100%-4rem))] lg:aspect-[1411/626] lg:w-[min(176.375rem,calc(100%-8rem))]"
        >
          <GroqImage
            src={data.mainImage.src}
            alt={data.mainImageAlt ?? ''}
            fill
            preload
            quality={100}
            sizes="(min-width: 993px) 74vw, 100vw"
            className="object-cover"
            style={
              hotspot ? { objectPosition: `${hotspot.x * 100}% ${hotspot.y * 100}%` } : undefined
            }
          />
        </motion.div>
      )}
    </>
  );
}
